/* eslint-disable max-lines */
'use client';
/* eslint-disable import/order */
import { type CSSProperties } from 'react';

/* eslint-disable-next-line import/no-empty-named-blocks */
import type {} from '@mui/lab/themeAugmentation';

import { common } from '@mui/material/colors';
import {
  alpha,
  createTheme as createMuiTheme,
  type PaletteColor,
  type PaletteColorOptions,
  type Theme,
} from '@mui/material/styles';

/* eslint-disable-next-line import/no-empty-named-blocks */
import type {} from '@mui/x-data-grid-premium/themeAugmentation';

/* eslint-disable-next-line import/no-empty-named-blocks */
import type {} from '@mui/x-date-pickers-pro/themeAugmentation';

/* eslint-disable-next-line import/no-empty-named-blocks */
import type {} from '@mui/x-tree-view-pro/themeAugmentation';

import { type Optional, type Required } from 'utility-types';

import { ThemeBreakpoints } from '../responsive/breakpoints';

import { interactableStyle, Interaction } from '../interaction';
import * as autofill from './autofill';
import * as elements from './elements';
import * as palette from './palette';
import * as util from './selectors';
import * as typography from './typography';

export interface StateColors {
  readonly borderColor: string;
}

export type StateColorSlot =
  | 'error'
  | 'info'
  | 'pending'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning';

declare module '@mui/material/styles' {
  interface Palette {
    action: {
      activatedOpacity: number;
      active: string;
      disabled: string;
      disabledBackground: string;
      disabledOpacity: number;
      focus: string;
      focusOpacity: number;
      hover: string;
      hoverOpacity: number;
      selected: string;
      selectedOpacity: number;
    };
    alert: {
      errorBgColor: string;
      errorIconColor: string;
      errorTextColor: string;
      infoBgColor: string;
      infoIconColor: string;
      infoTextColor: string;
      pendingBgColor: string;
      pendingIconColor: string;
      pendingTextColor: string;
      successBgColor: string;
      successIconColor: string;
      successTextColor: string;
      warningBgColor: string;
      warningIconColor: string;
      warningTextColor: string;
    };
    chip: PaletteColor;
    other: {
      avatarFill: string;
      sideNavBgColor: string;
      tooltipFill: string;
      yellowWarning: string;
    };
    pale: PaletteColor;
    pending: PaletteColor;
    state: Record<StateColorSlot, StateColors>;
    transparent: string;
    white: string;
  }

  interface PaletteOptions {
    alert?: {
      errorBgColor: string;
      errorIconColor: string;
      errorTextColor: string;
      infoBgColor: string;
      infoIconColor: string;
      infoTextColor: string;
      pendingBgColor: string;
      pendingIconColor: string;
      pendingTextColor: string;
      successBgColor: string;
      successIconColor: string;
      successTextColor: string;
      warningBgColor: string;
      warningIconColor: string;
      warningTextColor: string;
    };
    chip?: PaletteColorOptions;
    other?: {
      avatarFill: string;
      sideNavBgColor: string;
      tooltipFill: string;
      yellowWarning: string;
    };
    pale?: PaletteColorOptions;
    pending?: PaletteColorOptions;
    state?: Record<StateColorSlot, StateColors>;
    transparent?: string;
    white?: string;
  }

  type TypographyProperties =
    | 'fontFamily'
    | 'fontSize'
    | 'fontStyle'
    | 'fontWeight'
    | 'letterSpacing'
    | 'lineHeight'
    | 'textTransform';

  interface TypographyVariants {
    body1Bold: CSSProperties;
    body2Bold: CSSProperties;
    body2Italic: CSSProperties;
    fieldErrorText: CSSProperties;
    fieldHelperText: CSSProperties;
    fieldWarningText: CSSProperties;
    h7: CSSProperties;
    monospace: Optional<
      Required<Pick<CSSProperties, TypographyProperties>, TypographyProperties>,
      'fontFamily'
    >;
    overlineBold: CSSProperties;
  }

  interface TypographyVariantsOptions {
    body1Bold?: CSSProperties;
    body2Bold?: CSSProperties;
    body2Italic?: CSSProperties;
    button: CSSProperties;
    fieldErrorText: CSSProperties;
    fieldHelperText: CSSProperties;
    fieldWarningText: CSSProperties;
    h7?: CSSProperties;
    monospace: Optional<
      Required<Pick<CSSProperties, TypographyProperties>, TypographyProperties>,
      'fontFamily'
    >;
    overlineBold?: CSSProperties;
  }
}

declare module '@mui/material/Badge' {
  interface BadgePropsColorOverrides {
    pending: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    pale: true;
    pending: true;
  }
}

declare module '@mui/material/Checkbox' {
  interface CheckboxPropsColorOverrides {
    pending: true;
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides {
    pending: true;
  }
}

declare module '@mui/material/Switch' {
  interface SwitchPropsColorOverrides {
    pending: true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body1Bold: true;
    body2Bold: true;
    body2Italic: true;
    fieldErrorText: true;
    fieldHelperText: true;
    fieldWarningText: true;
    h7: true;
    monospace: true;
    overlineBold: true;
  }
}

enum MuiElementClass {
  AutocompleteInputRoot = '.MuiAutocomplete-inputRoot',
  Focused = '.Mui-focused',
  InputBase = '.MuiInputBase-root',
  InputBaseInput = '.MuiInputBase-input',
  InputLabel = '.MuiInputLabel-root',
  MultilineInputBase = '.MuiInputBase-multiline',
  MultilineInputBaseInput = '.MuiInputBase-inputMultiline',
  Select = '.MuiSelect-root',
  SelectSelect = '.MuiSelect-select',
  TextField = '.MuiTextField-root',
  UnfocusedInputLabel = '.MuiInputLabel-shrink',
}

const HelperTextTypography = {
  color: palette.Secondary.main,
  fontSize: '0.875rem',
  fontWeight: 400,
  letterSpacing: '0.17px',
  lineHeight: '20px',
} as const satisfies CSSProperties;

const And = <C extends `${'.' | ':'}${string}`>(className: C, ...rest: string[]): `&${string}` =>
  `&${className}${rest.join('')}`;

const InputBaseFontStyle = (size: elements.InputSize = elements.DefaultInputSize) =>
  ({
    fontSize: elements.Inputs.getFontSize(size),
    letterSpacing: typography.DefaultLetterSpacing,
    lineHeight: elements.Inputs.getLineHeight(size),
  }) as const satisfies CSSProperties;

const InputBaseStyle = (
  size: elements.InputSize = elements.DefaultInputSize,
  excludeHeightClasses: [string, ...string[]] = [
    MuiElementClass.AutocompleteInputRoot,
    MuiElementClass.MultilineInputBase,
    MuiElementClass.Select,
  ],
) => ({
  ...InputBaseFontStyle(size),
  minHeight: elements.Inputs.getHeight(size),
  [util.withoutClass(...excludeHeightClasses)]: { height: elements.Inputs.getHeight(size) },
});

/**
 * Style that is applied to an {@link InputLabel} component for a given custom (internal, not MUI)
 * input size, {@link elements.InputSize} - only when that {@link InputLabel} is being rendered
 * inside* of the input, select or other form control data entry element (and not on top).
 *
 * In the focused case, the {@link InputLabel} is rendered on top of the input on the top left,
 * which this style does not apply to.
 */
const UnfocusedInputLabelSizeStyle = (size: elements.InputSize = elements.DefaultInputSize) => ({
  ...InputBaseFontStyle(size),
  transform: elements.Inputs.getLabelTransform(size),
});

const UnfocusedInputLabelSelector = `:not(${MuiElementClass.UnfocusedInputLabel})`;

const InputFocusedLabelStyle = {
  color: palette.Secondary.main,
  fontWeight: 500,
  letterSpacing: typography.DefaultLetterSpacing,
};

const InputLabelSizedStyle = (size: elements.InputSize = elements.DefaultInputSize) => ({
  ...InputBaseFontStyle(size),
  '&.MuiInputLabel-outlined': {
    [And(elements.muiInputLabelSizeClass(size), elements.WithoutInputSizeDataAttr)]:
      UnfocusedInputLabelSizeStyle(size),
    [And(MuiElementClass.Focused)]: InputFocusedLabelStyle,
    [And(UnfocusedInputLabelSelector)]: UnfocusedInputLabelSizeStyle(size),
  },
});

const InputLabelStyle = {
  ...InputBaseFontStyle(),
  '&.MuiInputLabel-outlined': {
    [And(MuiElementClass.Focused)]: InputFocusedLabelStyle,
    [And(UnfocusedInputLabelSelector)]: {
      // This is the default case for no MUI size classes and no 'data-attr-size' attributes.
      ...UnfocusedInputLabelSizeStyle(),
      ...Object.values(elements.InputSize).reduce(
        (acc: Record<string, unknown>, size) => ({
          ...acc,
          [elements.withInputSize(size)]: UnfocusedInputLabelSizeStyle(size),
        }),
        {},
      ),
      /* Fallback sizing properties for backwards compatibility that target the Mui-specific size
         classes rather than the internal 'data-attr-size' attributes in cases where they may be
         missing. These are only applied in cases where the MUI size-specific classes are present
         and the 'data-attr-size' attribute is missing. */
      ...Object.values(elements.InputSize).reduce(
        (acc: Record<string, unknown>, size) => ({
          ...acc,
          [And(elements.muiInputLabelSizeClass(size), elements.WithoutInputSizeDataAttr)]:
            UnfocusedInputLabelSizeStyle(size),
        }),
        {},
      ),
    },
  },
};

const TextFieldStyle = (size: elements.InputSize = elements.DefaultInputSize) => ({
  [`& > ${MuiElementClass.InputBase}`]: {
    ...InputBaseStyle(size),
    [`& > ${MuiElementClass.InputBaseInput}`]: {
      height: 'auto',
      lineHeight: elements.Inputs.getLineHeight(size),
      padding: 0,
    },
    padding: elements.Inputs.getPadding(size),
  },
  [`& > ${MuiElementClass.InputLabel}:not(${MuiElementClass.UnfocusedInputLabel})`]: {
    ...InputBaseFontStyle(size),
    transform: elements.Inputs.getLabelTransform(size),
  },
});

const AutocompleteStyle = (size: elements.InputSize = elements.DefaultInputSize) => ({
  [`& > ${MuiElementClass.TextField}`]: {
    [`& > ${MuiElementClass.InputBase}`]: {
      ...InputBaseStyle(size, [MuiElementClass.MultilineInputBase]),
      [`& > ${MuiElementClass.InputBaseInput}`]: {
        height: 'auto',
        lineHeight: elements.Inputs.getLineHeight(size),
        padding: 0,
      },
      padding: elements.Inputs.getPadding(size),
    },
    [`& > ${MuiElementClass.InputLabel}:not(${MuiElementClass.UnfocusedInputLabel})`]:
      UnfocusedInputLabelSizeStyle(size),
  },
});

const SelectInputStyle = (size: elements.InputSize = elements.DefaultInputSize) => ({
  ...InputBaseFontStyle(size),
  height: 'auto',
  minHeight: elements.Inputs.getSelectInnerHeight(size),
  padding: elements.Inputs.getSelectPadding(size),
  paddingBottom: elements.Inputs.getVerticalPadding(size),
  paddingTop: elements.Inputs.getVerticalPadding(size),
});

const SelectStyle = (size: elements.InputSize = elements.DefaultInputSize) => ({
  ...InputBaseStyle(size),
  [`& > ${MuiElementClass.InputBaseInput}`]: SelectInputStyle(size),
  [`& > ${MuiElementClass.InputLabel}:not(${MuiElementClass.UnfocusedInputLabel})`]:
    UnfocusedInputLabelSizeStyle(size),
  [`& > ${MuiElementClass.SelectSelect}`]: SelectInputStyle(size),
});

export interface CreateThemeOptions {
  readonly fontFamilies: {
    readonly body: string;
    readonly heading: string;
    readonly monospace?: string;
  };
}

export const createTheme = (options?: CreateThemeOptions): Theme =>
  createMuiTheme({
    breakpoints: { values: ThemeBreakpoints },
    components: {
      MuiAccordion: {
        styleOverrides: { root: { '&:before': { height: '0' } } },
      },
      MuiAlert: {
        styleOverrides: {
          root: ({ ownerState, theme }) => {
            const severity = ownerState.severity || 'info';
            const bgColorMap = {
              error: theme.palette.alert.errorBgColor,
              info: theme.palette.alert.infoBgColor,
              success: theme.palette.alert.successBgColor,
              warning: theme.palette.alert.warningBgColor,
            };
            const textColorMap = {
              error: theme.palette.alert.errorTextColor,
              info: theme.palette.alert.infoTextColor,
              success: theme.palette.alert.successTextColor,
              warning: theme.palette.alert.warningTextColor,
            };
            return { backgroundColor: bgColorMap[severity], color: textColorMap[severity] };
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          listbox: { padding: 0 },
          paper: ({ theme }) => ({ ...theme.typography.body2 }),
          root: {
            [`&[data-attr-type="${elements.InternalizedAutocompleteType}"]`]: {
              ...AutocompleteStyle(),
              ...Object.values(elements.InputSize).reduce(
                (acc: Record<string, unknown>, size) => ({
                  ...acc,
                  [elements.withInputSize(size)]: AutocompleteStyle(size),
                }),
                {},
              ),
            },
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            border: 'none !important',
            outline: `1px solid ${palette.State.secondary.borderColor}`,
            outlineColor: palette.State.secondary.borderColor,
            outlineOffset: '-1px',
            outlineWidth: '1px',
          },
        },
      },
      MuiButton: {
        defaultProps: { disableFocusRipple: true, disableRipple: true, disableTouchRipple: true },
        styleOverrides: {
          root: {
            '&:hover, &:focus, &:focus-within, &:focus-visible': { boxShadow: 'none' },
            '.MuiTouchRipple-root': { display: 'none' },
            boxShadow: 'none',
            fontWeight: 700,
          },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { border: `1px solid ${palette.Divider}`, boxShadow: 'none' },
        },
      },
      MuiCardActionArea: {
        styleOverrides: { root: { '&:hover': { backgroundColor: common.white } } },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontFamily: options?.fontFamilies.body, fontSize: '0.875rem', fontWeight: 500 },
        },
      },
      MuiCircularProgress: {
        styleOverrides: { root: { color: palette.Primary.main } },
      },
      MuiCssBaseline: {
        styleOverrides: () => ({
          ...autofill.AutofillCssOverrides,
          'a:not(.MuiButton-root):not(.MuiChip-root):not(.MuiButtonBase-root)': { outline: 'none' },
          margin: 0,
          marginBlockEnd: 0,
          marginBlockStart: 0,
          ...(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'h7'] as const).reduce(
            (acc, headingTag) => ({
              ...acc,
              [headingTag]: {
                margin: 0,
                marginBlockEnd: 0,
                marginBlockStart: 0,
                marginBottom: 0,
                marginTop: 0,
              },
            }),
            {},
          ),
        }),
      },
      MuiDatePicker: {
        defaultProps: {
          slotProps: {
            popper: {
              sx: {
                '& .MuiPickersPopper-paper': {
                  outline: `1px solid ${palette.Divider}`,
                  outlineColor: palette.Divider,
                  outlineOffset: '-1px',
                  outlineWidth: '1px',
                },
              },
            },
          },
        },
      },
      MuiFormControl: {
        styleOverrides: { root: { '& > .MuiInputLabel-root': InputLabelStyle } },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: { ...InputLabelStyle, '& > .MuiTypography-root': InputBaseFontStyle() },
        },
      },
      MuiFormHelperText: {
        styleOverrides: { root: { ...HelperTextTypography, marginLeft: '4px' } },
      },
      MuiFormLabel: {
        styleOverrides: { root: InputLabelStyle },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            height: 'auto',
            lineHeight: elements.Inputs.getLineHeight(),
            [util.withoutClass(MuiElementClass.MultilineInputBaseInput)]: {
              padding: elements.Inputs.getPadding(),
            },
          },
          inputMultiline: { padding: 0 },
          multiline: { height: 'auto', minHeight: elements.Inputs.getHeight() },
          root: {
            ...InputBaseStyle(),
            ...Object.values(elements.InputSize).reduce(
              (acc: Record<string, unknown>, size) => ({
                ...acc,
                [elements.withInputSize(size)]: InputBaseStyle(size),
              }),
              {},
            ),
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            ...InputLabelSizedStyle(),
            ...Object.values(elements.InputSize).reduce(
              (acc: Record<string, unknown>, size) => ({
                ...acc,
                [elements.withInputSize(size)]: InputLabelSizedStyle(size),
              }),
              {},
            ),
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: ({ theme }) => ({
            '& .MuiSvgIcon-root': {
              '&.Mui-disabled': { color: theme.palette.action.disabled },
              color: theme.palette.secondary.main,
            },
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: () => ({
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: palette.Secondary.main,
            },
          }),
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0, variant: 'outlined' },
        styleOverrides: {
          root: params => ({
            '&:not(.MuiPickersPopper-paper)': {
              ...(params.ownerState.onClick && params.ownerState.variant === 'outlined'
                ? interactableStyle(params.theme, [
                    {
                      interactions: [
                        Interaction.Focus,
                        Interaction.FocusVisible,
                        Interaction.FocusWithin,
                      ],
                      style: { backgroundColor: palette.Action.hover },
                    },
                  ])
                : {}),
            },
            border: 'none',
            borderColor: 'unset',
            borderWidth: '0px',
            outlineOffset: '-1px',
            ...(params.ownerState.variant === 'outlined'
              ? {
                  outline: `1px solid ${palette.Divider}`,
                  outlineColor: palette.Divider,
                  outlineOffset: '-1px',
                  outlineWidth: '1px',
                }
              : {}),
            ...(params.ownerState.variant === 'elevation' && params.ownerState.elevation !== 0
              ? { boxShadow: '1px 1px 4px 1px rgb(0 0 0 / 8%)' }
              : {}),
          }),
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: {
            fontSize: '1.25rem',
            right: '7px',
          },
          root: {
            ...SelectStyle(),
            ...Object.values(elements.InputSize).reduce(
              (acc: Record<string, unknown>, size) => ({
                ...acc,
                [elements.withInputSize(size)]: SelectStyle(size),
              }),
              {},
            ),
            display: 'flex',
          },
          select: { display: 'flex' },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            '&:focus': {
              borderRadius: '4px',
              outline: `2px solid ${palette.Secondary.main}`,
              outlineColor: palette.Secondary.main,
              outlineOffset: '-2px',
              outlineWidth: '2px',
            },
          },
        },
      },
      MuiTab: {
        defaultProps: { disableFocusRipple: true, disableRipple: true, disableTouchRipple: true },
        styleOverrides: { root: elements.TabStyleOverrides },
      },
      MuiTabs: { styleOverrides: { root: elements.TabsStyleOverrides } },
      MuiTextField: {
        styleOverrides: {
          root: {
            ...TextFieldStyle(),
            ...Object.values(elements.InputSize).reduce(
              (acc: Record<string, unknown>, size) => ({
                ...acc,
                [elements.withInputSize(size)]: TextFieldStyle(size),
              }),
              {},
            ),
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          arrow: ({ theme }) => ({ color: theme.palette.other.tooltipFill }),
          tooltip: ({ theme }) => ({
            backgroundColor: theme.palette.other.tooltipFill,
            color: common.white,
            fontSize: '0.75rem',
            lineHeight: '1rem',
          }),
        },
      },
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            body1: 'p',
            body1Bold: 'p',
            body2: 'p',
            body2Bold: 'p',
            body2Italic: 'p',
            button: 'span',
            caption: 'span',
            h1: 'h1',
            h2: 'h2',
            h3: 'h3',
            h4: 'h4',
            h5: 'h5',
            h6: 'h6',
            h7: 'h6',
            overline: 'span',
            overlineBold: 'span',
            subtitle1: 'h6',
            subtitle2: 'h6',
          },
        },
      },
    },
    palette: {
      action: palette.Action,
      alert: {
        errorBgColor: palette.Error.light,
        errorIconColor: palette.Error.main,
        errorTextColor: palette.Error.main,
        infoBgColor: palette.Info.light,
        infoIconColor: palette.Info.main,
        infoTextColor: palette.Info.main,
        pendingBgColor: palette.Pending.light,
        pendingIconColor: palette.Pending.main,
        pendingTextColor: palette.Pending.main,
        successBgColor: palette.Success.light,
        successIconColor: palette.Success.main,
        successTextColor: palette.Success.main,
        warningBgColor: palette.Warning.light,
        warningIconColor: palette.Warning.main,
        warningTextColor: palette.Warning.main,
      },
      background: { default: common.white, paper: common.white },
      chip: palette.Chip,
      contrastThreshold: 4.5,
      divider: palette.Divider,
      error: palette.Error,
      grey: palette.Grey,
      info: palette.Info,
      other: {
        avatarFill: '#EBEEF1',
        sideNavBgColor: alpha(palette.Secondary.main, 0.02),
        tooltipFill: palette.Secondary.main,
        yellowWarning: palette.YellowWarning,
      },
      pale: {
        contrastText: common.black,
        dark: common.white,
        light: common.white,
        main: common.white,
      },
      pending: palette.Pending,
      primary: palette.Primary,
      secondary: palette.Secondary,
      state: palette.State,
      success: palette.Success,
      text: palette.Text,
      tonalOffset: 0.2,
      transparent: 'rgba(0, 0, 0, 0)',
      warning: palette.Warning,
      white: common.white,
    },
    typography: {
      body1: {
        fontSize: '1.125rem',
        fontWeight: 400,
        letterSpacing: typography.DefaultLetterSpacing,
        lineHeight: '28px',
      },
      body1Bold: {
        fontSize: '1.125rem',
        fontWeight: 700,
        letterSpacing: typography.DefaultLetterSpacing,
        lineHeight: '28px',
      },
      body2: { fontSize: '1rem', fontWeight: 400, letterSpacing: '0.17px', lineHeight: '24px' },
      body2Bold: { fontSize: '1rem', fontWeight: 700, letterSpacing: '0.17px', lineHeight: '24px' },
      body2Italic: {
        fontSize: '1rem',
        fontStyle: 'italic',
        fontWeight: 400,
        letterSpacing: '0.17px',
        lineHeight: '24px',
      },
      button: {
        fontSize: '0.875rem',
        fontWeight: 500,
        letterSpacing: '0.4px',
        lineHeight: '22px',
        textTransform: 'capitalize',
      },
      caption: {
        fontSize: '0.875rem',
        fontWeight: 500,
        letterSpacing: '0.4px',
        lineHeight: '22px',
      },
      fieldErrorText: { ...HelperTextTypography, color: palette.Error.main },
      fieldHelperText: HelperTextTypography,
      fieldWarningText: { ...HelperTextTypography, color: palette.YellowWarning },
      fontFamily: options?.fontFamilies.body,
      h1: {
        fontFamily: options?.fontFamilies.heading,
        fontSize: '3.5rem',
        fontWeight: 700,
        letterSpacing: '-1.5px',
        lineHeight: '3.5rem',
      },
      h2: {
        fontFamily: options?.fontFamilies.heading,
        fontSize: '3rem',
        fontWeight: 700,
        letterSpacing: '-0.5px',
        lineHeight: '3rem',
      },
      h3: {
        fontFamily: options?.fontFamilies.heading,
        fontSize: '2.5rem',
        fontWeight: 700,
        lineHeight: '2.5rem',
      },
      h4: {
        fontFamily: options?.fontFamilies.heading,
        fontSize: '2rem',
        fontWeight: 700,
        letterSpacing: '0.25px',
        lineHeight: '2rem',
      },
      h5: {
        fontFamily: options?.fontFamilies.heading,
        fontSize: '1.5rem',
        fontWeight: 700,
        lineHeight: '2rem',
      },
      h6: {
        fontFamily: options?.fontFamilies.heading,
        fontSize: '1.25rem',
        fontWeight: 600,
        letterSpacing: typography.DefaultLetterSpacing,
        lineHeight: '2rem',
      },
      h7: {
        fontFamily: options?.fontFamilies.heading,
        fontSize: '1rem',
        fontWeight: 400,
        letterSpacing: typography.DefaultLetterSpacing,
        lineHeight: '1.5rem',
      },
      monospace: {
        fontFamily: options?.fontFamilies.monospace ?? options?.fontFamilies.body ?? undefined,
        fontSize: '0.875rem',
        fontStyle: 'normal',
        fontWeight: 500,
        letterSpacing: typography.DefaultLetterSpacing,
        lineHeight: '1rem',
        textTransform: 'none',
      },
      overline: {
        fontSize: '0.875rem',
        fontWeight: 500,
        letterSpacing: '0.4px',
        lineHeight: '36px',
        textTransform: 'uppercase',
      },
      overlineBold: {
        fontSize: '0.875rem',
        fontWeight: 700,
        letterSpacing: '0.4px',
        lineHeight: '36px',
        textTransform: 'uppercase',
      },
      subtitle1: {
        fontSize: '1.125rem',
        fontWeight: 400,
        letterSpacing: typography.DefaultLetterSpacing,
        lineHeight: '28px',
      },
      subtitle2: {
        fontSize: '0.875rem',
        fontWeight: 500,
        letterSpacing: '0.1px',
        lineHeight: '22px',
      },
    },
  });
